import React, { useCallback } from 'react';
import { Spinner } from '@heroui/react';
import { IdView, Typography, UniversalTable } from '../../../../components';
import { EmptyContentClientIc, EmptyContentIc } from '../../../../components/icons';
import { useTranslations } from '../../../../contexts/translation.context';
import { CampaignsStatsTableProps, CampaignStats } from '../types';
import { getTableColumns, SPINNER_CLASSNAMES } from '../constants';

const formatCurrency = (input?: number | string | null): string => {
  if (input === null || input === undefined) {
    return '$0.00';
  }

  const numericValue = typeof input === 'string' ? Number(input) : input;

  if (!Number.isFinite(numericValue)) {
    return '$0.00';
  }

  return `$${numericValue.toFixed(2)}`;
};

const CampaignsStatsTable: React.FC<CampaignsStatsTableProps> = ({
  data,
  isLoading,
  totalCount,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
  sortBy,
  onSort,
  visibleColumns,
  onRowAction,
}) => {
  const { t } = useTranslations();

  // Filter columns based on visible columns
  const allColumns = getTableColumns(t);
  const columns = visibleColumns
    ? allColumns.filter((col) => visibleColumns.includes(col.key))
    : allColumns;

  const getDurationLabel = useCallback(
    (key: string, fallback: string) => {
      if (!t) return fallback;
      const translation = t(key);
      return translation && translation !== key ? translation : fallback;
    },
    [t]
  );

  const formatDuration = useCallback(
    (input?: number | string | null): string => {
      if (input === null || input === undefined) {
        return '';
      }

      const milliseconds = typeof input === 'string' ? Number(input) : input;

      if (!Number.isFinite(milliseconds) || milliseconds <= 0) {
        return '';
      }

      const minute = 60 * 1000;
      const hour = 60 * minute;
      const day = 24 * hour;
      const week = 7 * day;
      const month = 30 * day;
      const year = 365 * day;

      const units = [
        { label: getDurationLabel('campaignsStats.duration.year', 'yr'), ms: year },
        { label: getDurationLabel('campaignsStats.duration.month', 'mo'), ms: month },
        { label: getDurationLabel('campaignsStats.duration.week', 'wk'), ms: week },
        { label: getDurationLabel('campaignsStats.duration.day', 'd'), ms: day },
        { label: getDurationLabel('campaignsStats.duration.hour', 'h'), ms: hour },
        { label: getDurationLabel('campaignsStats.duration.minute', 'm'), ms: minute },
      ];

      for (let index = 0; index < units.length; index += 1) {
        const unit = units[index];

        if (milliseconds >= unit.ms) {
          const primaryValue = Math.floor(milliseconds / unit.ms);
          let result = `${primaryValue} ${unit.label}`;

          const remainder = milliseconds - primaryValue * unit.ms;
          const nextUnit = units[index + 1];

          if (nextUnit && remainder >= nextUnit.ms) {
            const secondaryValue = Math.floor(remainder / nextUnit.ms);
            if (secondaryValue > 0) {
              result += ` ${secondaryValue} ${nextUnit.label}`;
            }
          }

          return result;
        }
      }

      return `${Math.max(1, Math.round(milliseconds / 1000))} ${getDurationLabel('campaignsStats.duration.second', 's')}`;
    },
    [getDurationLabel]
  );

  const renderCell = useCallback(
    (campaign: CampaignStats, columnKey: string) => {
      const value = campaign[columnKey as keyof CampaignStats];

      switch (columnKey) {
        case 'id':
          return (
            <IdView textToCopy={value}>
              {/* @ts-ignore */}
              {value?.slice(-4)}
            </IdView>
          );
        case 'model':
          return (
            <Typography variant="body2" className="text-gray-700">
              {value ?? ''}
            </Typography>
          );
        case 'clicks':
        case 'fans':
        case 'one_buy':
          return (
            <Typography variant="body2" className="text-gray-700">
              {value !== null && value !== undefined ? value : ''}
            </Typography>
          );
        case 'click_percent':
        case 'one_buy_percent':
          return (
            <Typography variant="body2" className="text-gray-700">
              {value !== null && value !== undefined ? `${Number(value).toFixed(1)}%` : ''}
            </Typography>
          );
        case 'three_messages':
        case 'three_message': {
          const count = campaign.three_messages ?? campaign.three_message;
          return (
            <Typography variant="body2" className="text-gray-700">
              {count !== null && count !== undefined ? count : ''}
            </Typography>
          );
        }
        case 'three_messages_percent':
        case 'three_message_percent': {
          const percent = campaign.three_messages_percent ?? campaign.three_message_percent;
          return (
            <Typography variant="body2" className="text-gray-700">
              {percent !== null && percent !== undefined ? `${Number(percent).toFixed(1)}%` : ''}
            </Typography>
          );
        }
        case 'revenue': {
          const totalPurchases =
            campaign.revenue ?? campaign.revenue ?? (typeof value === 'number' ? value : null);
          return (
            <Typography variant="body2" className="text-gray-700">
              {formatCurrency(totalPurchases)}
            </Typography>
          );
        }
        case 'apru':
          return (
            <Typography variant="body2" className="text-gray-700">
              {value !== null && value !== undefined ? `$${Number(value).toFixed(2)}` : ''}
            </Typography>
          );
        case 'active_period':
          return (
            <Typography variant="body2" className="text-gray-700">
              {formatDuration(value)}
            </Typography>
          );
        default:
          return (
            <Typography variant="body2" className="text-gray-700">
              {value !== null && value !== undefined ? String(value) : ''}
            </Typography>
          );
      }
    },
    [formatDuration]
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" classNames={SPINNER_CLASSNAMES} />
      </div>
    );
  }

  return (
    <UniversalTable
      mobileSize="2xl"
      data={data}
      columns={columns}
      renderCell={renderCell}
      // onRowAction={onRowAction}
      emptyContent={
        <div className="text-center py-12">
          <div className="flex flex-col items-center gap-4">
            <EmptyContentIc />
            <div className="flex flex-col gap-1.5">
              <Typography variant="h6" className="text-xl font-semibold leading-7">
                {t('campaignsStats.noCampaignsFound') || 'Нет данных'}
              </Typography>
              <Typography
                className="text-neutral-500 text-base font-medium leading-6"
                variant="body1"
              >
                {t('campaignsStats.checkDataOrTryAgain') || 'Проверьте данные или попробуйте снова'}
              </Typography>
            </div>
          </div>
        </div>
      }
      pagination={{
        count: totalCount,
        page_size: pageSize,
        page: currentPage,
      }}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      currentPage={currentPage}
      currentPageSize={pageSize}
      sortBy={sortBy}
      onSort={onSort}
    />
  );
};

export default CampaignsStatsTable;
